import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Toaster } from "../components/ui/toaster";
import { useToast } from "../hooks/use-toast";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock,
  Heart,
  Sprout,
  TrendingUp,
} from "lucide-react";
import { fundingPaths } from "../mock";

const FundingApplyPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    org: "",
    path: "",
    creditAbove700: "",
    hasBusinessCC: "",
    hasActiveLLC: "",
    highestCCLimit: "",
    capitalSeeking: "",
    summary: "",
  });
  const { toast } = useToast();

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@") || !form.path) {
      toast({
        title: "A few details missing",
        description: "Please fill in name, a valid email, and pick a funding path.",
      });
      return;
    }
    if (
      !form.creditAbove700 ||
      !form.hasBusinessCC ||
      !form.hasActiveLLC ||
      !form.highestCCLimit ||
      !form.capitalSeeking
    ) {
      toast({
        title: "Credit questions required",
        description: "Please answer all 5 prequalification questions.",
      });
      return;
    }
    const list = JSON.parse(
      localStorage.getItem("good_fruit_funding_intake") || "[]"
    );
    list.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("good_fruit_funding_intake", JSON.stringify(list));
    setSubmitted(true);
    toast({
      title: "Intake received",
      description: "We'll contact you within 5 business days.",
    });
  };

  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />

      <section className="relative pt-28 md:pt-36 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#04181a] to-black" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 75% 25%, #2dd4bf 0%, transparent 45%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10">
          <Link
            to="/funding"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-teal-300 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back to funding paths
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 text-teal-200 px-3 py-1.5 rounded-full text-xs tracking-wide">
            <Clock className="w-3.5 h-3.5" />
            <span>LMI partner site launching soon</span>
          </div>
          <h1 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.1]">
            Funding intake —{" "}
            <em className="text-teal-300 font-serif font-normal">interim form.</em>
          </h1>
          <p className="mt-4 text-white/65 text-base md:text-lg leading-relaxed">
            Our partner site at LMI Marketing & Business Solutions Group, Inc.
            is launching shortly. Until it goes live, you can submit your
            information here and we&apos;ll personally route you through
            prequalification within 5 business days.
          </p>
        </div>
      </section>

      <section className="bg-white text-black py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          {submitted ? (
            <div className="rounded-2xl border border-teal-600/30 bg-[#f6faf9] p-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-400 text-black">
                <CheckCircle2 className="w-8 h-8" strokeWidth={2.5} />
              </div>
              <h2 className="mt-5 text-2xl md:text-3xl font-semibold tracking-tight text-black">
                Intake received.
              </h2>
              <p className="mt-3 text-black/60 max-w-md mx-auto">
                We&apos;ll review your submission and reach out within 5 business
                days. In the meantime, take a look at the Finance Course to
                strengthen your application.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/finance-course">
                  <Button className="bg-black hover:bg-teal-700 text-white font-semibold h-11 px-6">
                    Open Finance Course
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button
                    variant="outline"
                    className="border-black/20 text-black hover:bg-black/5"
                  >
                    Back home
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="rounded-2xl border border-black/10 bg-white p-7 md:p-10 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Your name
                  </label>
                  <Input
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="Pastor John Doe"
                    className="h-11"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email: e.target.value }))
                    }
                    placeholder="you@church.org"
                    className="h-11"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Organization or business name
                </label>
                <Input
                  value={form.org}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, org: e.target.value }))
                  }
                  placeholder="Hope Chapel, Mended Vessels Inc., etc."
                  className="h-11"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Which funding path?
                </label>
                <Select
                  value={form.path}
                  onValueChange={(v) => setForm((f) => ({ ...f, path: v }))}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Choose a funding path" />
                  </SelectTrigger>
                  <SelectContent>
                    {fundingPaths.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.title} — {p.range}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Credit prequalification section */}
              <div className="pt-2">
                <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-semibold">
                  Credit prequalification
                </div>
                <div className="mt-1 text-xs text-black/55">
                  Honest answers help us route you to the right path faster.
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Is your personal credit above 700 in all 3 bureaus?
                  </label>
                  <Select
                    value={form.creditAbove700}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, creditAbove700: v }))
                    }
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select an answer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes, all three above 700</SelectItem>
                      <SelectItem value="mostly">Two of three above 700</SelectItem>
                      <SelectItem value="no">No, none above 700</SelectItem>
                      <SelectItem value="unsure">I&apos;m not sure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Do you have any business credit cards?
                  </label>
                  <Select
                    value={form.hasBusinessCC}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, hasBusinessCC: v }))
                    }
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select an answer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="applying">Currently applying</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Active LLC (or 501c3) in good standing?
                  </label>
                  <Select
                    value={form.hasActiveLLC}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, hasActiveLLC: v }))
                    }
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select an answer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes_llc">Yes — LLC in good standing</SelectItem>
                      <SelectItem value="yes_501c3">Yes — 501(c)(3) in good standing</SelectItem>
                      <SelectItem value="forming">Currently forming one</SelectItem>
                      <SelectItem value="no">Not yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Highest limit on any credit card?
                  </label>
                  <Select
                    value={form.highestCCLimit}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, highestCCLimit: v }))
                    }
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Pick a range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under_2k">Under $2,000</SelectItem>
                      <SelectItem value="2k_5k">$2,000 – $5,000</SelectItem>
                      <SelectItem value="5k_15k">$5,000 – $15,000</SelectItem>
                      <SelectItem value="15k_30k">$15,000 – $30,000</SelectItem>
                      <SelectItem value="30k_50k">$30,000 – $50,000</SelectItem>
                      <SelectItem value="50k_plus">$50,000+</SelectItem>
                      <SelectItem value="none">No credit cards</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-black mb-2">
                    How much capital are you seeking?
                  </label>
                  <Select
                    value={form.capitalSeeking}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, capitalSeeking: v }))
                    }
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Pick a range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1k_5k">$1,000 – $5,000</SelectItem>
                      <SelectItem value="5k_25k">$5,000 – $25,000</SelectItem>
                      <SelectItem value="25k_75k">$25,000 – $75,000</SelectItem>
                      <SelectItem value="75k_150k">$75,000 – $150,000</SelectItem>
                      <SelectItem value="150k_250k">$150,000 – $250,000</SelectItem>
                      <SelectItem value="250k_plus">$250,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  In a few sentences, what are you trying to build?
                </label>
                <Textarea
                  value={form.summary}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, summary: e.target.value }))
                  }
                  placeholder="Share the calling, the people you serve, and where you are today."
                  rows={5}
                />
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <div className="text-xs text-black/55 max-w-md">
                  We treat your information like the trust it is. We will never
                  sell your data, and we will only use it to walk you through
                  prequalification.
                </div>
                <Button
                  type="submit"
                  className="bg-black hover:bg-teal-700 text-white font-semibold h-11 px-6 shrink-0"
                >
                  Submit intake
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          )}

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {fundingPaths.map((p) => {
              const Icon =
                p.icon === "Heart"
                  ? Heart
                  : p.icon === "TrendingUp"
                    ? TrendingUp
                    : Sprout;
              return (
                <div
                  key={p.id}
                  className="rounded-xl border border-black/10 bg-[#f6faf9] p-5"
                >
                  <div className="w-9 h-9 rounded-lg bg-teal-500/10 border border-teal-600/20 text-teal-700 flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="mt-3 text-sm font-semibold text-black">
                    {p.title}
                  </div>
                  <div className="mt-1 text-xs text-black/55">{p.range}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default FundingApplyPage;
